/**
 * Prism chat catalog — client-safe metadata for the composer UI.
 *
 * Models map 1:1 to the platform `ModelTier`, which the Azure AI Foundry
 * provider resolves to a concrete deployment. The `/api/prism/catalog`
 * route enriches each entry with the live deployment name + availability.
 *
 * Skills are pre-built "glorified Skills" that map a one-click action to a
 * Prism workflow (or a templated prompt for general chat). Connectors mirror
 * the Knowledge Foundation provider plane (Tavily, FMP, etc.).
 */
import type { ModelTier, PrismWorkflow } from "@/lib/prism/types";

export type PrismModelId = ModelTier;

export interface PrismModelOption {
    id: PrismModelId;
    label: string;
    /** Short positioning line shown under the model name. */
    description: string;
    /** Resolved Foundry deployment name (server-filled). */
    deployment?: string;
    /** True when the tier has a configured Foundry deployment. */
    available: boolean;
}

/** Static model menu. The route overlays `deployment` + `available`. */
export const PRISM_MODELS: PrismModelOption[] = [
    {
        id: "flash",
        label: "Prism Fast",
        description: "Quick answers and routing",
        available: false,
    },
    {
        id: "pro",
        label: "Prism Pro",
        description: "Balanced reasoning (default)",
        available: false,
    },
    {
        id: "premium",
        label: "Prism Max",
        description: "Deepest analysis for hard questions",
        available: false,
    },
];

export const DEFAULT_PRISM_MODEL_ID: PrismModelId = "pro";

export type PrismSkillModule = "monitoring" | "origination" | "strategy" | "prism";

export interface PrismSkill {
    id: string;
    name: string;
    description: string;
    module: PrismSkillModule;
    /** When set, the skill forces a specific Prism workflow. */
    workflow?: PrismWorkflow;
    /** Prompt the composer pre-fills when the skill is picked. */
    promptTemplate: string;
    builtin: boolean;
}

/** Pre-built skills for our module workflows (the differentiator vs generic chat). */
export const PRISM_BUILTIN_SKILLS: PrismSkill[] = [
    {
        id: "portfolio-attention",
        name: "Portfolio attention",
        description: "Rank what needs intervention across the portfolio",
        module: "monitoring",
        workflow: "monitoring.attention",
        promptTemplate: "What needs my attention across the portfolio?",
        builtin: true,
    },
    {
        id: "company-intervention",
        name: "Company intervention plan",
        description: "Intervention playbook for the focused company",
        module: "monitoring",
        workflow: "monitoring.intervention",
        promptTemplate: "What interventions do we need for {company}?",
        builtin: true,
    },
    {
        id: "daily-brief",
        name: "Daily brief",
        description: "Morning brief across plan, actuals and signals",
        module: "prism",
        workflow: "prism.brief",
        promptTemplate: "Give me the morning brief",
        builtin: true,
    },
    {
        id: "find-targets",
        name: "Find targets",
        description: "Source thesis-aligned targets for a theme",
        module: "origination",
        workflow: "origination.search_targets",
        promptTemplate: "Find best targets for {theme}",
        builtin: true,
    },
    {
        id: "company-deep-dive",
        name: "Company deep-dive",
        description: "Synthesize a company across knowledge + signals",
        module: "prism",
        workflow: "prism.general_chat",
        promptTemplate: "Give me a deep-dive on {company} — performance, risks and the value-creation angle.",
        builtin: true,
    },
    {
        id: "thesis-drift",
        name: "Thesis drift check",
        description: "Where are actuals drifting from the thesis?",
        module: "monitoring",
        workflow: "monitoring.thesis_drift",
        promptTemplate: "Where are our portfolio actuals drifting from the original thesis for {company}?",
        builtin: true,
    },
    {
        id: "burning-questions",
        name: "Burning questions",
        description: "Highest-leverage diligence questions for a screening target",
        module: "origination",
        workflow: "origination.burning_questions",
        promptTemplate: "Generate burning questions for {target}",
        builtin: true,
    },
    {
        id: "conflict-check",
        name: "Conflict check",
        description: "Cross-portfolio overlap and synergy scan for a target",
        module: "origination",
        workflow: "origination.conflict_check",
        promptTemplate: "Run a conflict check for {target}",
        builtin: true,
    },
    {
        id: "memo-draft",
        name: "Draft screening memo",
        description: "Partner-grade IC memo for a screening target",
        module: "origination",
        workflow: "origination.memo_draft",
        promptTemplate: "Draft a screening memo for {target}",
        builtin: true,
    },
    {
        id: "generate-soap",
        name: "Generate SOAP",
        description: "CSO strategic foundation (SOAP) for a portfolio company",
        module: "strategy",
        workflow: "strategy.soap",
        promptTemplate: "Write a SOAP strategic foundation for {company}",
        builtin: true,
    },
    {
        id: "brainstorm-pathways",
        name: "Brainstorm pathways",
        description: "Strategy pathways for a critical gap",
        module: "strategy",
        workflow: "strategy.brainstorm",
        promptTemplate: "Brainstorm strategies for the growth gap at {company}",
        builtin: true,
    },
    {
        id: "founder-background",
        name: "Founder background",
        description: "Management team profile with news grounding",
        module: "origination",
        workflow: "origination.founder_background",
        promptTemplate: "Profile the founder and management team for {target}",
        builtin: true,
    },
];

export type PrismConnectorCategory = "news" | "company" | "financials" | "documents" | "people";

export interface PrismConnector {
    id: string;
    name: string;
    description: string;
    category: PrismConnectorCategory;
    tier: "platform" | "tenant";
    /** True when the connector has credentials configured (server-filled). */
    configured: boolean;
}

/** Connector plane mirrored from the Knowledge Foundation provider tiers. */
export const PRISM_CONNECTORS: PrismConnector[] = [
    {
        id: "tavily",
        name: "Tavily",
        description: "Live news and web signals",
        category: "news",
        tier: "platform",
        configured: false,
    },
    {
        id: "bing-news",
        name: "Bing News",
        description: "Supplementary news search",
        category: "news",
        tier: "platform",
        configured: false,
    },
    {
        id: "crunchbase",
        name: "Crunchbase",
        description: "Company funding and investor data",
        category: "company",
        tier: "platform",
        configured: false,
    },
    {
        id: "fmp",
        name: "Financial Modeling Prep",
        description: "Public-market multiples and comps",
        category: "financials",
        tier: "platform",
        configured: false,
    },
    {
        id: "azure-di",
        name: "Azure Document Intelligence",
        description: "Parse decks, CIMs and financial PDFs",
        category: "documents",
        tier: "platform",
        configured: false,
    },
    {
        id: "microsoft-graph",
        name: "Microsoft Graph",
        description: "SharePoint / OneDrive document ingest",
        category: "documents",
        tier: "tenant",
        configured: false,
    },
];

export interface PrismCatalog {
    models: PrismModelOption[];
    skills: PrismSkill[];
    connectors: PrismConnector[];
    defaultModelId: PrismModelId;
    /** Azure Speech dictation in composer (server-filled). */
    speechInput?: { available: boolean };
}
